import { HTMLAttributes, useRef } from 'react'
import { useIsomorphicLayoutEffect } from 'react-use'
import gsap from 'gsap'

import ClipPath from '@/components/base/ClipPath'
import { useRouterStore } from '@/store/router'
import { tunnel } from '@/webgl/tunnel'

import { cn } from '@/lib/utils'

export type LandingProps = HTMLAttributes<HTMLDivElement>

export const getClipPathRatio = () => {
  const vw = window.innerWidth
  const vh = window.innerHeight
  return Math.min(1, vw / vh) * 0.1
}

const Landing = ({ className, ...props }: LandingProps) => {
  const root = useRef<HTMLDivElement>(null!)
  const mask = useRef<SVGSVGElement>(null!)

  useIsomorphicLayoutEffect(() => {
    const unsubscribe = useRouterStore.subscribe(
      (state) => [state.currentRoute, state.previousRoute],
      ([currentRoute, previousRoute]) => {
        if (currentRoute === previousRoute) return

        const selector = gsap.utils.selector(mask.current)
        const path = selector('path')

        if (currentRoute === '/') {
          document.body.classList.add('home')

          gsap.timeline().to(path, {
            keyframes: [
              {
                attr: { d: `M 0 0 L 1 0 L 1 0 L 0 1z` },
                duration: 1
              },
              {
                attr: { d: `M 0 0 L 1 0 L 1 1 L 0 1z` },
                duration: 1,
                delay: -0.5
              }
            ],
            duration: 1.3,
            ease: 'power2.inOut'
          })
        } else if (!previousRoute || previousRoute === '/') {
          document.body.classList.remove('home')

          gsap.timeline().to(path, {
            keyframes: [
              {
                attr: { d: `M 0 0 L 1 0 L 1 0 L 0 1z` },
                duration: 1
              },
              {
                attr: { d: `M 0 0 L 1 0 L 1 0 L 0 0z` },
                duration: 1,
                delay: -0.5
              }
            ],
            duration: 1.3,
            ease: 'power2.inOut'
          })
        }
      },
      {
        fireImmediately: true
      }
    )

    return () => {
      unsubscribe()
    }
  }, [])

  return (
    <div
      ref={root}
      className={cn(
        'fixed left-0 top-0 z-20 h-full w-full touch-none overflow-hidden',
        '[clip-path:url(#landing-mask)]',
        className
      )}
      {...props}
    >
      <tunnel.Out />

      <ClipPath ref={mask} id="landing-mask" />
    </div>
  )
}

export default Landing
